/**
 * Singleton registry for extension handlers.
 *
 * Populated at startup before the server starts.
 * Throws on duplicate type registration.
 */
import type { ExtensionHandler } from "@shared/extensions/_interface.ts"

export class ExtensionRegistry {
  private handlers = new Map<string, ExtensionHandler>()

  register(handler: ExtensionHandler): void {
    if (this.handlers.has(handler.type)) {
      throw new Error(`Extension type "${handler.type}" is already registered`)
    }
    this.handlers.set(handler.type, handler)
  }

  getHandler(type: string): ExtensionHandler | undefined {
    return this.handlers.get(type)
  }

  getHandlerOrThrow(type: string): ExtensionHandler {
    const handler = this.handlers.get(type)
    if (!handler) {
      throw new Error(`Unknown extension type: "${type}"`)
    }
    return handler
  }

  getAllHandlers(): ExtensionHandler[] {
    return Array.from(this.handlers.values())
  }

  hasType(type: string): boolean {
    return this.handlers.has(type)
  }

  get types(): string[] {
    return Array.from(this.handlers.keys())
  }
}
